import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Header } from "@/components/layout/Header";
import { StepIndicator } from "@/components/layout/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { servicesData } from "@/data/mockData";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import {
  User,
  Calendar,
  Clock,
  Activity,
  Heart,
  Droplets,
  Gauge,
  Thermometer,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Download,
  Printer,
  FileSignature,
  Hospital
} from "lucide-react";

// Normal ranges for medical values
const normalRanges = {
  glicemia: { min: 70, max: 100, unit: "mg/dL" },
  systolic: { min: 90, max: 120, unit: "mmHg" },
  diastolic: { min: 60, max: 80, unit: "mmHg" },
  spo2: { min: 95, max: 100, unit: "%" },
  heartRate: { min: 60, max: 100, unit: "bpm" },
  temperature: { min: 36.0, max: 37.2, unit: "°C" },
  cholesterol: { min: 0, max: 200, unit: "mg/dL" },
  triglycerides: { min: 0, max: 150, unit: "mg/dL" }
};

// Mock ECG data
const ecgData = Array.from({ length: 50 }, (_, i) => ({
  t: i,
  v: Math.sin(i * 0.5) * 20 + (i % 10 === 5 ? 80 : 0) + Math.random() * 5
}));

export const ResultsPage = () => {
  const navigate = useNavigate();
  const { selectedPatient, selectedServices, deviceReadings, setSessionResults, sessionResults } = useAppContext();
  const [results, setResults] = useState([]);

  // Determine if any selected service requires medical reporting
  const reportingServices = useMemo(
    () => selectedServices
      .map(sid => servicesData.find(s => s.id === sid))
      .filter(s => s && s.requiresReport),
    [selectedServices]
  );
  const needsReporting = reportingServices.length > 0;

  // Process readings into results
  useEffect(() => {
    const processedResults = [];
    
    Object.entries(deviceReadings).forEach(([deviceId, data]) => {
      const { reading, device, services } = data;
      
      // Process based on device type
      if (deviceId === "glucometer") {
        processedResults.push({
          id: "glicemia",
          name: "Glicemia",
          value: reading.value,
          unit: reading.unit,
          status: getStatus(reading.value, normalRanges.glicemia),
          icon: Droplets,
          category: "Analisi"
        });
      }
      
      if (deviceId === "bloodPressure") {
        processedResults.push({
          id: "pressione_sistolica",
          name: "Pressione Sistolica",
          value: reading.systolic,
          unit: reading.unit,
          status: getStatus(reading.systolic, normalRanges.systolic),
          icon: Gauge,
          category: "Parametri"
        });
        processedResults.push({
          id: "pressione_diastolica",
          name: "Pressione Diastolica",
          value: reading.diastolic,
          unit: reading.unit,
          status: getStatus(reading.diastolic, normalRanges.diastolic),
          icon: Gauge,
          category: "Parametri"
        });
        processedResults.push({
          id: "frequenza",
          name: "Frequenza Cardiaca",
          value: reading.pulse,
          unit: "bpm",
          status: getStatus(reading.pulse, normalRanges.heartRate),
          icon: Heart,
          category: "Parametri"
        });
      }
      
      if (deviceId === "oximeter") {
        processedResults.push({
          id: "spo2",
          name: "Saturazione O2 (SpO2)",
          value: reading.spo2,
          unit: reading.unit,
          status: getStatus(reading.spo2, normalRanges.spo2),
          icon: Activity,
          category: "Parametri"
        });
      }
      
      if (deviceId === "ecg") {
        processedResults.push({
          id: "ecg",
          name: "ECG",
          value: reading.heartRate,
          unit: reading.unit,
          rhythm: reading.rhythm,
          status: "normal",
          icon: Heart,
          category: "Diagnostica",
          hasChart: true
        });
      }
      
      if (deviceId === "thermometer") {
        processedResults.push({
          id: "temperatura",
          name: "Temperatura",
          value: reading.value,
          unit: reading.unit,
          status: getStatus(reading.value, normalRanges.temperature),
          icon: Thermometer,
          category: "Parametri"
        });
      }
      
      if (deviceId === "scale") {
        processedResults.push({
          id: "peso",
          name: "Peso",
          value: reading.weight,
          unit: reading.unit,
          status: "normal",
          icon: Gauge,
          category: "Parametri"
        });
        processedResults.push({
          id: "bmi",
          name: "BMI",
          value: reading.bmi,
          unit: "",
          status: reading.bmi < 18.5 ? "low" : reading.bmi > 25 ? "high" : "normal",
          icon: Gauge,
          category: "Parametri"
        });
      }
      
      if (deviceId === "analyzer") {
        if (reading.cholesterol) {
          processedResults.push({
            id: "colesterolo",
            name: "Colesterolo Totale",
            value: reading.cholesterol,
            unit: "mg/dL",
            status: getStatus(reading.cholesterol, normalRanges.cholesterol),
            icon: Droplets,
            category: "Analisi"
          });
        }
        if (reading.triglycerides) {
          processedResults.push({
            id: "trigliceridi",
            name: "Trigliceridi",
            value: reading.triglycerides,
            unit: "mg/dL",
            status: getStatus(reading.triglycerides, normalRanges.triglycerides),
            icon: Droplets,
            category: "Analisi"
          });
        }
        if (reading.wbc) {
          processedResults.push({
            id: "globuli_bianchi",
            name: "Globuli Bianchi (WBC)",
            value: reading.wbc,
            unit: "K/µL",
            status: "normal",
            icon: Droplets,
            category: "Emocromo"
          });
        }
        if (reading.rbc) {
          processedResults.push({
            id: "globuli_rossi",
            name: "Globuli Rossi (RBC)",
            value: reading.rbc,
            unit: "M/µL",
            status: "normal",
            icon: Droplets,
            category: "Emocromo"
          });
        }
        if (reading.hgb) {
          processedResults.push({
            id: "emoglobina",
            name: "Emoglobina (HGB)",
            value: reading.hgb,
            unit: "g/dL",
            status: "normal",
            icon: Droplets,
            category: "Emocromo"
          });
        }
      }
      
      if (deviceId === "spirometer") {
        processedResults.push({
          id: "fev1",
          name: "FEV1",
          value: reading.fev1,
          unit: reading.unit,
          status: "normal",
          icon: Activity,
          category: "Spirometria"
        });
        processedResults.push({
          id: "fvc",
          name: "FVC",
          value: reading.fvc,
          unit: reading.unit,
          status: "normal",
          icon: Activity,
          category: "Spirometria"
        });
        processedResults.push({
          id: "fev1_fvc",
          name: "FEV1/FVC",
          value: reading.ratio,
          unit: "%",
          status: reading.ratio >= 70 ? "normal" : "low",
          icon: Activity,
          category: "Spirometria"
        });
      }
    });
    
    setResults(processedResults);
    setSessionResults({
      patient: selectedPatient,
      results: processedResults,
      timestamp: new Date().toISOString()
    });
  }, [deviceReadings, selectedPatient, setSessionResults]);

  const getStatus = (value, range) => {
    if (value < range.min) return "low";
    if (value > range.max) return "high";
    return "normal";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "normal": return "text-success";
      case "low": return "text-info";
      case "high": return "text-warning";
      default: return "text-muted-foreground";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "normal": 
        return <Badge className="bg-success/10 text-success border-success/20 gap-1"><CheckCircle2 className="h-3 w-3" />Normale</Badge>;
      case "low": 
        return <Badge className="bg-info/10 text-info border-info/20 gap-1"><TrendingDown className="h-3 w-3" />Basso</Badge>;
      case "high": 
        return <Badge className="bg-warning/10 text-warning border-warning/20 gap-1"><TrendingUp className="h-3 w-3" />Alto</Badge>;
      default: 
        return <Badge variant="outline" className="gap-1"><Minus className="h-3 w-3" />N/A</Badge>;
    }
  };

  // Group results by category
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.category]) {
      acc[result.category] = [];
    }
    acc[result.category].push(result);
    return acc;
  }, {});

  const handleContinue = () => {
    if (needsReporting) {
      navigate("/reporting-routing");
    } else {
      navigate("/export");
    }
  };

  // Count alerts (high/low values)
  const alerts = results.filter(r => r.status === "high" || r.status === "low").length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Risultati" />
      <StepIndicator currentStep={4} />
      
      <main className="flex-1 px-4 pb-6 md:px-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Patient info */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{selectedPatient?.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">
                      {selectedPatient?.fiscalCode}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date().toLocaleDateString('it-IT')}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <div className="flex flex-col md:flex-row gap-4">
            <Card className="flex-1">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-success/10 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{results.length}</p>
                  <p className="text-sm text-muted-foreground">Parametri rilevati</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className={cn("flex-1", alerts > 0 && "border-warning/50")}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={cn(
                  "h-12 w-12 rounded-xl flex items-center justify-center",
                  alerts > 0 ? "bg-warning/10" : "bg-success/10"
                )}>
                  {alerts > 0 ? (
                    <AlertTriangle className="h-6 w-6 text-warning" />
                  ) : (
                    <CheckCircle2 className="h-6 w-6 text-success" />
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold">{alerts}</p>
                  <p className="text-sm text-muted-foreground">
                    {alerts > 0 ? "Valori fuori norma" : "Tutti nella norma"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Page header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Risultati Esami
            </h1>
            <div className="flex gap-2">
              <Button variant="outline" size="icon">
                <Printer className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Results by category */}
          <ScrollArea className="h-[400px] md:h-[450px]">
            <div className="space-y-6 pr-4">
              {Object.entries(groupedResults).map(([category, categoryResults], catIndex) => (
                <div key={category} className="animate-fade-in-up" style={{ animationDelay: `${catIndex * 100}ms` }}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                    {category}
                  </h3>
                  <div className="space-y-3">
                    {categoryResults.map((result, index) => {
                      const Icon = result.icon;
                      
                      return (
                        <Card
                          key={result.id}
                          className={cn(
                            "transition-all",
                            result.status === "high" && "result-warning",
                            result.status === "low" && "border-info/50",
                            result.status === "normal" && "result-success"
                          )}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={cn(
                                  "p-2 rounded-lg",
                                  result.status === "normal" && "bg-success/10",
                                  result.status === "high" && "bg-warning/10",
                                  result.status === "low" && "bg-info/10"
                                )}>
                                  <Icon className={cn(
                                    "h-5 w-5",
                                    result.status === "normal" && "text-success",
                                    result.status === "high" && "text-warning",
                                    result.status === "low" && "text-info"
                                  )} />
                                </div>
                                <div>
                                  <h4 className="font-medium">{result.name}</h4>
                                  {result.rhythm && (
                                    <p className="text-sm text-muted-foreground">Ritmo: {result.rhythm}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={cn("text-2xl font-bold", getStatusColor(result.status))}>
                                  {result.value}
                                  <span className="text-sm font-normal text-muted-foreground ml-1">
                                    {result.unit}
                                  </span>
                                </p>
                                {getStatusBadge(result.status)}
                              </div>
                            </div>
                            
                            {/* ECG Chart */}
                            {result.hasChart && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="text-xs text-muted-foreground mb-2">Tracciato ECG</p>
                                <div className="h-24 w-full">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={ecgData}>
                                      <defs>
                                        <linearGradient id="ecgGradient" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                                          <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                                        </linearGradient>
                                      </defs>
                                      <Area
                                        type="monotone"
                                        dataKey="v"
                                        stroke="hsl(var(--success))"
                                        strokeWidth={1.5}
                                        fill="url(#ecgGradient)"
                                      />
                                    </AreaChart>
                                  </ResponsiveContainer>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Continue button */}
          <Card className="sticky bottom-4 shadow-lg border-2">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <p className="font-medium">Esami completati</p>
                  <p className="text-sm text-muted-foreground">
                    {needsReporting
                      ? `${reportingServices.length} ${reportingServices.length === 1 ? "esame richiede" : "esami richiedono"} refertazione medica`
                      : "Procedi all'esportazione e condivisione dei risultati"}
                  </p>
                </div>
                <Button
                  variant="premium"
                  size="lg"
                  onClick={handleContinue}
                  className="gap-2"
                  data-testid="results-continue-btn"
                >
                  {needsReporting ? (
                    <>
                      <Hospital className="h-4 w-4" />
                      Seleziona struttura di refertazione
                    </>
                  ) : (
                    <>
                      Esporta Risultati
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ResultsPage;